import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, FileText, Download, Trash2, X, File } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency, formatFileSize } from "@/lib/utils";

interface CarrierCommissionStatementsTabProps {
  statementMonth: string;
}

interface CarrierStatement {
  id: number;
  carrierId: number | null;
  statementMonth: string;
  fileName: string;
  fileSize: number;
  status: string;
  totalCommission: string | null;
  notes: string | null;
  uploadedAt: string;
  uploadedBy: string;
}

function formatStatementMonth(yyyyMm: string, variant: "long" | "short" = "long"): string {
  // Avoid timezone drift: `new Date("YYYY-MM-01")` is parsed as UTC midnight and can render as the prior month in US timezones.
  const [y, m] = yyyyMm.split("-").map((v) => Number(v));
  if (!y || !m) return yyyyMm;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleDateString("en-US", {
    month: variant,
    year: "numeric",
    timeZone: "UTC",
  });
}

export function CarrierCommissionStatementsTab({ statementMonth }: CarrierCommissionStatementsTabProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadMonth, setUploadMonth] = useState(statementMonth);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [totalCommission, setTotalCommission] = useState("");
  const [filterMonth, setFilterMonth] = useState(statementMonth);
  const [generatingAgencyPdf, setGeneratingAgencyPdf] = useState(false);
  const [generatingAgentId, setGeneratingAgentId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch clients
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/clients"],
  });

  // Fetch all carriers
  const { data: allCarriers = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/carriers"],
  });

  // Fetch all plans to get carriers used by the agency
  const { data: allPlans = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions/plans"],
  });

  // Get unique carrier IDs from plans associated with agency's clients
  const clientCarrierIds = new Set<number>();
  
  // Get carrier IDs from client associations
  clients.forEach(client => {
    if (client.carrierId) {
      clientCarrierIds.add(client.carrierId);
    }
  });
  
  // Also get carrier IDs from plans
  allPlans.forEach(plan => {
    if (plan.carrierId) {
      clientCarrierIds.add(plan.carrierId);
    }
  });

  // Filter carriers to only those used by agency
  const carriers = allCarriers
    .filter(carrier => clientCarrierIds.has(carrier.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Fetch statements
  const { data: statements = [], isLoading } = useQuery<CarrierStatement[]>({
    queryKey: ["/api/carrier-statements", { statementMonth: filterMonth }],
    enabled: !!filterMonth,
  });

  // Fetch commissions for agency statement calculations
  const { data: commissions = [] } = useQuery<any[]>({
    queryKey: ["/api/commissions"],
  });

  // Generate last 12 months for filter dropdown
  const availableMonths = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return date.toISOString().slice(0, 7);
  });

  // Fetch all client account managers to get split information
  const { data: allClientAccountManagers = [] } = useQuery<any[]>({
    queryKey: ["/api/client-account-managers"],
  });

  // Fetch agency employees to get agent names (same as Agency.tsx does)
  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees", "agency"],
    queryFn: () => fetch("/api/employees").then(res => res.json()).then(data => 
      // Filter to show only employees not assigned to any client (agency employees)
      data.filter((emp: any) => !emp.clientId && !emp.prospectId)
    ),
  });

  // Fetch split profiles
  const { data: agencyAgentSplitProfiles = [] } = useQuery<any[]>({
    queryKey: ["/api/commission-split-profiles", "agency_agent"],
    queryFn: () => fetch("/api/commission-split-profiles?splitType=agency_agent").then(res => res.json()),
  });

  const { data: primarySecondarySplitProfiles = [] } = useQuery<any[]>({
    queryKey: ["/api/commission-split-profiles", "primary_secondary"],
    queryFn: () => fetch("/api/commission-split-profiles?splitType=primary_secondary").then(res => res.json()),
  });

  // Calculate agency statement data for the selected month
  const agencyStatementData = (() => {
    // Filter commissions by accounting month (when they were recorded)
    const monthCommissions = commissions.filter(c => c.accountingMonth === filterMonth);
    
    // Calculate totals
    const totalPremium = monthCommissions.reduce((sum, c) => {
      const premium = parseFloat(c.actualPremium || c.projectedPremium || '0');
      return sum + premium;
    }, 0);
    
    const totalCommission = monthCommissions.reduce((sum, c) => {
      const commission = parseFloat(c.actualCommission || c.projectedCommission || '0');
      return sum + commission;
    }, 0);
    
    // Calculate actual agency retention and agent payouts using client-level agency/agent split.
    let agencyRetentionTotal = 0;
    let agentPayoutsTotal = 0;
    
    monthCommissions.forEach(c => {
      const commission = parseFloat(c.actualCommission || c.projectedCommission || '0');
      if (commission === 0) return;

      // Determine if this client has any producers assigned.
      const clientProducers = allClientAccountManagers.filter(
        (m: any) => m.clientId === c.clientId && m.role && m.role.toLowerCase().includes('producer')
      );

      if (clientProducers.length === 0) {
        // If no producers assigned, all goes to agency.
        agencyRetentionTotal += commission;
        return;
      }

      // Client-level agency/agent split profile.
      const clientMeta = clients.find((cl: any) => cl.id === c.clientId);
      const splitProfile = clientMeta?.agencyAgentSplitId
        ? agencyAgentSplitProfiles.find((p: any) => p.id === clientMeta.agencyAgentSplitId)
        : agencyAgentSplitProfiles.find((p: any) => p.isDefault);

      const agencyPercent = splitProfile?.agencyPercentage || 40;
      const agentPercent = splitProfile?.agentPercentage || 60;

      const agentPortion = commission * (agentPercent / 100);
      const agencyPortion = commission * (agencyPercent / 100);

      agencyRetentionTotal += agencyPortion;
      agentPayoutsTotal += agentPortion;
    });
    
    // Calculate actual percentages based on totals
    const agencyRetentionPercent = totalCommission > 0 
      ? (agencyRetentionTotal / totalCommission) * 100 
      : 40;
    const agentPayoutsPercent = totalCommission > 0 
      ? (agentPayoutsTotal / totalCommission) * 100 
      : 60;
    
    // Check if all commission records are completed (have actual values)
    const allCompleted = monthCommissions.length > 0 && monthCommissions.every(c => 
      c.actualPremium && c.actualCommission
    );
    
    const status = monthCommissions.length === 0 
      ? 'no_data' 
      : allCompleted 
        ? 'ready' 
        : 'pending';
    
    return {
      totalPremium,
      totalCommission,
      agencyRetention: agencyRetentionTotal,
      agentPayouts: agentPayoutsTotal,
      agencyRetentionPercent,
      agentPayoutsPercent,
      recordCount: monthCommissions.length,
      status
    };
  })();

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error("No file selected");

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("statementMonth", uploadMonth);
      if (selectedCarrierId) formData.append("carrierId", selectedCarrierId);
      if (notes) formData.append("notes", notes);
      if (totalCommission) formData.append("totalCommission", totalCommission);

      const response = await fetch("/api/carrier-statements/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Upload failed");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carrier-statements"] });
      toast({
        title: "Statement uploaded",
        description: "The carrier commission statement has been uploaded successfully.",
      });
      setUploadDialogOpen(false);
      resetUploadForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/carrier-statements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carrier-statements"] });
      toast({
        title: "Statement deleted",
        description: "The statement has been deleted successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Delete failed",
        description: "Failed to delete the statement.",
        variant: "destructive",
      });
    },
  });

  // Download handler
  const handleDownload = async (statementId: number) => {
    try {
      const response = await fetch(`/api/carrier-statements/${statementId}/download`, {
        credentials: "include",
      });
      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (error) {
      toast({
        title: "Download failed",
        description: "Failed to generate download URL.",
        variant: "destructive",
      });
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setUploadMonth(statementMonth);
    setSelectedCarrierId("");
    setNotes("");
    setTotalCommission("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a file smaller than 10MB.",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const filteredStatements = statements.filter(s => s.statementMonth === filterMonth);

  // Helpers to download server-generated PDFs
  const downloadPdf = async (url: string, filename: string) => {
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) {
      let message = `Failed to generate PDF (${resp.status})`;
      try {
        const data = await resp.json();
        if (data?.message) message = data.message;
      } catch {}
      throw new Error(message);
    }
    const blob = await resp.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  const handleGenerateAgencyPdf = async () => {
    try {
      setGeneratingAgencyPdf(true);
      await downloadPdf(
        `/api/commission-statements/agency/${filterMonth}`,
        `agency-statement-${filterMonth}.pdf`
      );
    } catch (error: any) {
      toast({ title: "Generation failed", description: error?.message || "Unable to generate agency PDF", variant: "destructive" });
    } finally {
      setGeneratingAgencyPdf(false);
    }
  };

  const handleGenerateAgentPdf = async (agentId: string, agentName: string) => {
    try {
      setGeneratingAgentId(agentId);
      await downloadPdf(
        `/api/commission-statements/agent/${agentId}/${filterMonth}`,
        `agent-statement-${agentName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${filterMonth}.pdf`
      );
    } catch (error: any) {
      toast({ title: "Generation failed", description: error?.message || "Unable to generate agent PDF", variant: "destructive" });
    } finally {
      setGeneratingAgentId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Label htmlFor="monthFilter" className="whitespace-nowrap">Statement Month:</Label>
        <Input
          id="monthFilter"
          type="month"
          className="h-8 w-48 cursor-pointer"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          onMouseDown={(e) => {
            e.preventDefault();
            const el = e.currentTarget;
            el.focus();
            (el as any).showPicker?.();
          }}
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <div>
                <CardTitle>Carrier Commission Statements</CardTitle>
                <CardDescription className="mt-1">
                  Viewing statements for {formatStatementMonth(filterMonth, "long")}
                </CardDescription>
              </div>
            </div>
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Upload className="h-4 w-4 mr-2" />
                Upload Statement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Carrier Commission Statement</DialogTitle>
                <CardDescription>Upload and manage commission statements from insurance carriers</CardDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="carrier">Carrier *</Label>
                  <Select value={selectedCarrierId} onValueChange={setSelectedCarrierId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      {carriers.map((carrier) => (
                        <SelectItem key={carrier.id} value={carrier.id.toString()}>
                          {carrier.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">Statement File (PDF) *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="file"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
                      className="cursor-pointer"
                    />
                  </div>
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <File className="h-4 w-4" />
                      <span>{selectedFile.name}</span>
                      <span>({formatFileSize(selectedFile.size)})</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="statementMonth">Statement Month *</Label>
                  <Input
                    id="statementMonth"
                    type="month"
                    value={uploadMonth}
                    onChange={(e) => setUploadMonth(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="totalCommission">Total Commission (Optional)</Label>
                  <Input
                    id="totalCommission"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={totalCommission}
                    onChange={(e) => setTotalCommission(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes about this statement..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setUploadDialogOpen(false);
                      resetUploadForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => uploadMutation.mutate()}
                    disabled={!selectedFile || !uploadMonth || !selectedCarrierId || uploadMutation.isPending}
                  >
                    {uploadMutation.isPending ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading statements...</div>
        ) : filteredStatements.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No carrier statements uploaded for this month
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Upload carrier commission statements to track and reconcile payments
            </p>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Upload Statement
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Total Commission</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStatements.map((statement) => {
                const carrier = carriers.find(c => c.id === statement.carrierId);
                return (
                  <TableRow key={statement.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        {statement.fileName}
                      </div>
                    </TableCell>
                    <TableCell>{carrier?.name || "—"}</TableCell>
                    <TableCell>
                      {formatStatementMonth(statement.statementMonth, "short")}
                    </TableCell>
                    <TableCell className="text-right">
                      {statement.totalCommission
                        ? formatCurrency(Number(statement.totalCommission).toFixed(2))
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(statement.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm("Are you sure you want to delete this statement?")) {
                              deleteMutation.mutate(statement.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {statements.length > 0 && filteredStatements.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No statements found for the selected month. Use the dropdown above to change the viewing month.
          </div>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            <div>
              <CardTitle>Agency Statements (PDF)</CardTitle>
              <CardDescription className="mt-1">
                Agency-level commission summary based on accounting data
              </CardDescription>
            </div>
          </div>
          <Button 
            disabled={agencyStatementData.status !== 'ready' || generatingAgencyPdf}
            onClick={handleGenerateAgencyPdf}
          >
            <Download className="h-4 w-4 mr-2" />
            {generatingAgencyPdf ? 'Generating…' : 'Generate PDF'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {agencyStatementData.status === 'no_data' ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No commission data for this month
            </p>
            <p className="text-sm text-muted-foreground">
              Commission records will appear here once added to the accounting data
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="space-y-1 bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Premium</p>
                <p className="text-2xl font-semibold">{formatCurrency(agencyStatementData.totalPremium.toFixed(2))}</p>
              </div>
              <div className="space-y-1 bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Total Commission</p>
                <p className="text-2xl font-semibold">{formatCurrency(agencyStatementData.totalCommission.toFixed(2))}</p>
              </div>
              <div className="space-y-1 bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Agency Retention ({agencyStatementData.agencyRetentionPercent.toFixed(1)}%)</p>
                <p className="text-2xl font-semibold text-green-600">{formatCurrency(agencyStatementData.agencyRetention.toFixed(2))}</p>
              </div>
              <div className="space-y-1 bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Agent Payouts ({agencyStatementData.agentPayoutsPercent.toFixed(1)}%)</p>
                <p className="text-2xl font-semibold text-blue-600">{formatCurrency(agencyStatementData.agentPayouts.toFixed(2))}</p>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Agency Commission Statement - {formatStatementMonth(filterMonth, "long")}</p>
                    <p className="text-sm text-muted-foreground">{agencyStatementData.recordCount} commission records</p>
                  </div>
                </div>
                {agencyStatementData.status === 'ready' ? (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">
                    Ready
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200">
                    Data Pending
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          <div>
            <CardTitle>Agent Statements (PDF)</CardTitle>
            <CardDescription className="mt-1">
              Individual agent commission statements based on accounting data and split assignments
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {agencyStatementData.status === 'no_data' ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-muted-foreground mb-2">
              No commission data for this month
            </p>
            <p className="text-sm text-muted-foreground">
              Agent statements will appear here once commission records are added
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(() => {
              // Calculate commission totals per agent
              const monthCommissions = commissions.filter(c => c.accountingMonth === filterMonth);
              const agentCommissions = new Map<string, { userId: string; name: string; total: number; recordCount: number; hasIncompleteRecords: boolean }>();

              monthCommissions.forEach(c => {
                const commission = parseFloat(c.actualCommission || c.projectedCommission || '0');
                
                // Find all agents for this client
                const clientAgents = allClientAccountManagers.filter(
                  (manager: any) => manager.clientId === c.clientId && 
                  manager.role && (manager.role.includes('producer') || manager.role.includes('Producer'))
                );
                
                if (clientAgents.length === 0) return;
                
                clientAgents.forEach((manager: any) => {
                  // Agency/Agent split is client-scoped.
                  const clientMeta = clients.find((cl: any) => cl.id === c.clientId);
                  const agentSplitProfile = clientMeta?.agencyAgentSplitId
                    ? agencyAgentSplitProfiles.find((p: any) => p.id === clientMeta.agencyAgentSplitId)
                    : agencyAgentSplitProfiles.find((p: any) => p.isDefault);
                  
                  const agentPercent = agentSplitProfile?.agentPercentage || 60;
                  
                  // Calculate agent's portion based on agency/agent split
                  const agentPortion = commission * (agentPercent / 100);
                  
                  // If there are multiple agents (primary/secondary), split the agent portion
                  const primaryAgent = clientAgents.find((a: any) => a.role && a.role.toLowerCase().includes('primary'));
                  const secondaryAgent = clientAgents.find((a: any) => a.role && a.role.toLowerCase().includes('secondary'));
                  
                  let finalAgentPortion = agentPortion;
                  
                  if (primaryAgent && secondaryAgent && clientAgents.length > 1) {
                    // Get primary/secondary split
                    const primarySecondarySplit = manager.primarySecondarySplitId
                      ? primarySecondarySplitProfiles.find((p: any) => p.id === manager.primarySecondarySplitId)
                      : primarySecondarySplitProfiles.find((p: any) => p.isDefault);
                    
                    const primaryPercent = primarySecondarySplit?.primaryPercentage || 50;
                    const secondaryPercent = primarySecondarySplit?.secondaryPercentage || 50;
                    
                    // Calculate the split portion
                    const isPrimary = manager.role && manager.role.toLowerCase().includes('primary');
                    const splitPercent = isPrimary ? primaryPercent : secondaryPercent;
                    finalAgentPortion = agentPortion * (splitPercent / 100);
                  }

                  // Aggregate by agent
                  const agentKey = manager.userId;
                  
                  // Look up agent name from employees (same as Agency.tsx does)
                  const employee = employees.find((emp: any) => emp.id?.toString() === agentKey);
                  const agentName = employee 
                    ? `${employee.firstName} ${employee.lastName}`
                    : manager.email || `Agent ${agentKey}`;
                  
                  // Check if this commission record is complete (same logic as agency statements)
                  const isComplete = c.actualPremium && c.actualCommission;
                  
                  const existing = agentCommissions.get(agentKey);
                  
                  if (existing) {
                    existing.total += finalAgentPortion;
                    existing.recordCount += 1;
                    // If ANY record is incomplete, mark agent as having incomplete records
                    if (!isComplete) {
                      existing.hasIncompleteRecords = true;
                    }
                  } else {
                    agentCommissions.set(agentKey, {
                      userId: agentKey,
                      name: agentName,
                      total: finalAgentPortion,
                      recordCount: 1,
                      hasIncompleteRecords: !isComplete
                    });
                  }
                });
              });              const agentStatements = Array.from(agentCommissions.values()).map(agent => ({
                ...agent,
                status: agent.hasIncompleteRecords ? 'pending' : 'ready'
              })).sort((a, b) => b.total - a.total);

              if (agentStatements.length === 0) {
                return (
                  <div className="text-center py-8 text-muted-foreground">
                    No agents assigned to commission records for this month
                  </div>
                );
              }

              return agentStatements.map((agent) => (
                <div key={agent.userId} className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{agent.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {agent.userId} • {agent.recordCount} commission record{agent.recordCount !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-2xl font-semibold">{formatCurrency(agent.total.toFixed(2))}</p>
                        <p className="text-sm text-muted-foreground">Commission Earned</p>
                      </div>
                      {agent.status === 'ready' ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-950/20 dark:text-green-200">
                          Ready
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-950/20 dark:text-yellow-200">
                          Data Pending
                        </Badge>
                      )}
                      <Button 
                        disabled={agent.status !== 'ready' || generatingAgentId === agent.userId}
                        onClick={() => handleGenerateAgentPdf(agent.userId, agent.name)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {generatingAgentId === agent.userId ? 'Generating…' : 'Generate PDF'}
                      </Button>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
}
